import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';

import { Subscription } from 'rxjs';

import { ProminenceFilterApiModel, ProminenceRangeFilterApiModel } from '../../../../api/mentions/model/prominence-filter-api.model';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { AbstractFilterTypeComponent } from '../../../../Common/Filter/models/abstract-filter-type.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ProminenceLockerService } from '../../../../Common/Misc/services/prominence-locker.service';
import { ProminenceLevel, ProminenceScoreModel } from '../../models/prominence-score.model';
import { CheckboxGroup, ProminenceFilterModel } from './prominence-filter.model';

@Component({
    selector: 'agpr-prominence-filter',
    templateUrl: './prominence-filter.component.html',
    styleUrls: ['./prominence-filter.component.scss'],
    providers: [
        {
            provide: AbstractFilterTypeComponent,
            useExisting: ProminenceFilterComponent
        }
    ],
    standalone: false
})
export class ProminenceFilterComponent extends AbstractFilterTypeComponent implements OnChanges, OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly severity = messageSeverityEnum;
    readonly prominenceJobStatuses = ProminenceRecalculationJobStatus;

    @Input() declare filter: ProminenceFilterModel;

    filteredOptions: CheckboxGroup[];

    // Value is represented as a list of keys like
    // id-My Group-VERY_HIGH
    // so they can be mapped easily to checkboxes
    // Then it has to be converted back into ranges that the backend can interpret
    value: string[] = [];

    anyOptionsSelected: boolean = false;
    searchQuery: string;
    showAddCustomButton: boolean = false;

    expanded = {};

    valueHashes = [];

    prominenceStatus: ProminenceRecalculationJobStatus;
    statusSubscription: Subscription;

    constructor(prominenceLockerService: ProminenceLockerService){
        super();
        this.statusSubscription = prominenceLockerService.prominenceStatus$.subscribe(status => this.prominenceStatus = status);
    }

    ngOnInit() {
        this.searchQuery = '';
        this.refresh();
    }

    ngOnChanges(): void {
        this.searchQuery = '';
        this.refresh();
    }

    ngOnDestroy(): void {
        this.statusSubscription.unsubscribe();
    }

    /**
     * Refreshes the current value of the filter
     */
    refresh(): void {
        this.search();
        this.value = [];

        if (this.filter.getCurrentValue()) {
            this.filter.getCurrentValue().filters.forEach(filter => {
                for (const range of filter.ranges) {
                    this.value.push(
                        filter.prominenceGroup.id + '-' +
                        filter.prominenceGroup.name + '-' +
                        ProminenceScoreModel.getProminenceLevel(range.max));
                }
            });

            this.anyOptionsSelected = this.value.length > 0;
        }
    }

    /**
     * Updates the current value of the filter
     */
    handleChange(): void {
        const promFilter = new ProminenceFilterApiModel();
        for (const key of this.value) {
            let separatorIndex = key.lastIndexOf('-');
            const level: ProminenceLevel = ProminenceLevel[key.substring(separatorIndex + 1)];
            const groupIdAndName: string = key.substring(0, separatorIndex);
            separatorIndex = groupIdAndName.indexOf('-');
            const groupId = +groupIdAndName.substring(0, separatorIndex);
            const groupName = groupIdAndName.substring(separatorIndex + 1);


            let filterGroup = promFilter.filters.find(x => x.prominenceGroup.id === groupId);
            if (!filterGroup) {
                filterGroup = new ProminenceRangeFilterApiModel();
                filterGroup.prominenceGroup = {
                    id: groupId,
                    name: groupName
                };
                promFilter.filters.push(filterGroup);
            }
            filterGroup.ranges.push(ProminenceScoreModel.toRange(level));
        }
        this.filter.setCurrentValue(promFilter);

        this.anyOptionsSelected = this.value.length > 0;

    }

    /**
     * Selects all of the options
     */
    selectAll(): void {
        // this.value = this.filter.options.map((option) => option.value);
        this.handleChange();
    }

    /**
     * Deselects all of the options
     */
    selectNone(): void {
        this.value = [];
        this.handleChange();
    }

    search(): void {
        this.filteredOptions = this.filter?.options?.slice() || [];
        this.showAddCustomButton = false;
        if (this.searchQuery) {
            let hasExactMatch = false;
            const queryLower = this.searchQuery.toLocaleLowerCase();
            this.filteredOptions = this.filteredOptions.filter(option => {
                const optionLower = option.label.toLocaleLowerCase();

                if (optionLower === queryLower) {
                    hasExactMatch = true;
                    return true;
                }

                return optionLower.includes(queryLower);
            });
        }
    }

    toggleFilterExpansion(option: CheckboxGroup, event?: any): void {
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }

        this.expanded[option.label] = !this.expanded[option.label];
    }
}
